import React, { useEffect, useState } from "react";
import StepContent from "../../components/step-content";
import Header from "../../components/header";
import { PlanType } from "../../components/enums/plan-type";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import {
  RequiredNumber,
  OptionalCheckbox,
  OptionalNumber,
  RequiredString,
  RequiredBoolean,
} from "../../components/form_elements/form-validators";
import VoluntaryLife from "./voluntary-life";
import LifeInsurancePlanDetails from "./lifeinsurance-plan-details";
import { StructuredListSkeleton } from "carbon-components-react";
import useMsalApi from "../../components/use-msal-api";
import {
  addAttachment,
  updateContacts,
  updateDependent,
  updateInsurance,
} from "../../redux/actions/enrollments-actions";
import Beneficiaries from "./beneficaries";
import BeneficiaryEdit from "./beneficiary-edit";
import { BeneficiaryType } from "../../components/enums/beneficiary-type";
import { Button } from "carbon-components-react";
import { Add16 } from "@carbon/icons-react";
import CompanyPaid from "./company-paid";
import { BenefitGroup } from "../../components/enums/benefit-group";
import ErrorSummary from "../../components/form_elements/error-summary";
import { format, parseISO } from "date-fns";
import { getQualifiedDependents, isQualifiedDependent } from "../../components/contact-builder";

const LifeInsurance = ({ pageStep }) => {
  const [volLife, setVolLife] = useState([]);
  const [spouseLife, setSpouseLife] = useState([]);
  const [childLife, setChildLife] = useState([]);
  const [beneficiary, setBeneficiary] = useState();
  const [show, setShow] = useState();
  const [isEmployee, setIsEmployee] = useState(false);
  const [reset, setReset] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [validationErrors, setValidationErrors] = useState();
  const [callApi] = useMsalApi();
  const dispatch = useDispatch();
  const [refreshCompanyPaid, setRefreshCompanyPaid] = useState(null);

  const enrollments = useSelector((s) => s.enrollments);

  const validationSchema = Yup.object().shape({
  planOption: RequiredString("Required"),
  dependents: Yup.array().of(Yup.object().shape({
      id: RequiredString("Required"),
      enrolled : RequiredBoolean("Required")
    })).test('has-dependents', 'You must select at least one dependent for Basic Life Employee + Dependents Tier',
      (item, ctx) => {
        var options = ["2"];
        if(options.includes(ctx.parent.planOption))
        {
          return item?.filter(s => s.enrolled === true)?.length > 0;
        } else {
          return true;
        }
      }
    ),
    employeeBenefit: OptionalCheckbox(),
    spouseBenefit: OptionalCheckbox(),
    childBenefit: OptionalCheckbox(),
    spousePaid: OptionalCheckbox(),
    childPaid: OptionalCheckbox(),
    employeeAmount: OptionalNumber()
      .max(enrollments?.employee?.maxCoverageAmount)
      .when("employeeBenefit", { is: true, then: RequiredNumber("Required") })
      .test("employee-amount-ok","Amount must be greater than or equal to 10000", (value) => {return value >= 10000 || value === null}),
    spouseAmount: OptionalNumber()
      .min(5000)
      .when("spouseBenefit", {
        is: true,
        then: RequiredNumber("Required").min(5000),
      })
      .test(
        "spouse-amount-ok",
        "Amount must be 50% or less of employee amount",
        (item, ctx) => {
          return ctx.parent.employeeAmount / 2 >= item;
        }
      ),
    childAmount: OptionalNumber()
      .max(10000)
      .min(5000)
      .when("childBenefit", { is: true, then: RequiredNumber("Required") })
      .test(
        "child-amount-ok",
        "Amount must be 50% or less of employee amount",
        (item, ctx) => {
          return ctx.parent.employeeAmount / 2 >= item;
        }
      ),


      primaryPercentage: OptionalNumber().test("primary-percent", "% of benefit for all primary beneficiaries must total 100%",
      () => {
        const primaries = beneficiaries?.filter(s => s.type === 1);
        if(!primaries || primaries.length === 0)
        return true;
       const amount = primaries.map(item => item.percentage).reduce((prev, next) => prev + next);
        return  amount === 100;
        }
    ).test("primary-required", "You must have at least one primary beneficiary on record", () => {
      const primaries = beneficiaries?.filter(s => s.type === 1);
      if(!primaries || primaries.length === 0)
        return false;
      return true;
  }),
  
  contingentPercentage: OptionalNumber().test("contingent-percent", "% of benefit for all contingent beneficiaries must total 100%",
    () => {
      const contingents = beneficiaries?.filter(s => s.type === 2);
      if(!contingents || contingents.length === 0)
        return true;
      const amount = contingents.map(item => item.percentage).reduce((prev, next) => prev + next);
      return  amount === 100;
    }
  )
  });


  const onSubmit = async (formData) => {
    const frm = validationSchema.cast(formData);
    setValidationErrors(null);
    let params = {
      selections: [],
      contacts: beneficiaries,
    };
    let basicLife = "LIFEBAS";
    let basicLifeOptions = "ER LIFE";
    if (enrollments.employee.benefitGroup === BenefitGroup.Executive) {
      basicLife = "LIFEEXEC";
      basicLifeOptions = "EX LIFE";
    }
    const basicLifePlanName = enrollments.plans.find((s) => s.id === basicLife).name;
    const basicLifeOptionsPlanName = enrollments.plans.find((s) => s.id === basicLifeOptions).name;

    let lifeBasic = {
      planId: basicLife,
      planDescription: basicLifePlanName,
      planType: PlanType.BeneficiaryLife,
      beneficiaries: beneficiaries,
    };

    let lifeBasicOption = {
      planId: basicLifeOptions,
      planDescription: basicLifeOptionsPlanName,
      planType: PlanType.BasicLife,
      planOption: formData.planOption,
    };
    let newDependents = [];

      lifeBasicOption.dependents = [];
      var qualifiedDependents = getQualifiedDependents(enrollments, beneficiaries);
      console.log("QDeps", beneficiaries);
      beneficiaries.forEach(b => {
        if(isQualifiedDependent(b) && !qualifiedDependents.some(q => q.id === b.id)){
          newDependents.push({...b});
        }
      });
      console.log("New Dps", newDependents);

      var companyPaidDependents = qualifiedDependents.concat(newDependents);
      console.log("All Dps", companyPaidDependents);
     
      companyPaidDependents.forEach(d => {
        lifeBasicOption.dependents.push(d.id);
      });
      if(lifeBasicOption.dependents > 0)
        lifeBasicOption.planOption = "2";

    params.selections.push(lifeBasic, lifeBasicOption);

    if (formData.employeeBenefit === true) {
      params.selections.push(addSelection(volLife, formData.employeeAmount));
      if (formData.spouseBenefit === true)
        params.selections.push(addSelection(spouseLife, formData.spouseAmount));
      else params.selections.push(addSelection(spouseLife, null));
      if (formData.childBenefit === true)
        params.selections.push(addSelection(childLife, formData.childAmount));
      else params.selections.push(addSelection(childLife, null));
    } else {
      params.selections.push(addSelection(volLife, null));
      params.selections.push(addSelection(spouseLife, null));
      params.selections.push(addSelection(childLife, null));
    }
    
    const results = await callApi(`Enrollments/${enrollments.id}/insurance/${enrollments.employee.id}/${format(
      parseISO(enrollments.effectiveDate),
      "yyyy-MM-dd"
    )}`, "PATCH", params);
    if (!results.error) {
      dispatch(updateInsurance(results.selections));
      if (results.contacts.length > 0)
        dispatch(updateContacts(results.contacts));
        if(newDependents.length > 0){
          newDependents.forEach(d => {
            dispatch(updateDependent(d));
          });
        } 
  
      await attachments.forEach(async a =>  {
        const frmData = new FormData();
        frmData.append("file", a.file);

        const uploadResult = await callApi(`Attachment/${enrollments.id}/${a.type}`, "POST", frmData, "multipart/form-data");
        if(!uploadResult.error){
            dispatch(addAttachment(uploadResult));
        }
      });

    }
  };


  const onReset = () => {
    setReset(!reset);
  };

  const addSelection = (plan, amount) => {
    return {
      planId: plan.id,
      planDescription: plan.name,
      planType: plan.planType,
      amount: amount,
    };
  };

  const onError = (errors, e) => {
    console.log("Form Submission Error", e);
  };



  useEffect(() => {
    if (Array.isArray(enrollments)) 
      return;
      
    const volPlan = enrollments.plans.find(
      (plan) => plan.planType === PlanType.VoluntaryLife
    );
    
    setVolLife(volPlan);
    const childPlan = enrollments.plans.find(
      (plan) => plan.planType === PlanType.ChildLife
    );
    setChildLife(childPlan);
    const spousePlan = enrollments.plans.find(
      (plan) => plan.planType === PlanType.SpouseLife
    );
    setSpouseLife(spousePlan);
    
  }, [enrollments]);

  return (
    <>
      <StepContent
        pageStep={pageStep}
        schema={validationSchema}
        onSubmit={onSubmit}
        onError={onError}
        onReset={onReset}
      >
        <div className="max-w-screen-lg">
          <div className="flex flex-col sm:flex-row items-center mb-4 mt-2">
            <div className="text-3xl sm:mr-16">Life Insurance</div>
            <div className="sm:mr-16">
              <LifeInsurancePlanDetails />
            </div>
          </div>
          {!Array.isArray(enrollments) ? (
            <>
              <CompanyPaid reset={reset} refreshCompanyPaid={refreshCompanyPaid} setRefreshCompanyPaid={setRefreshCompanyPaid}  />
              <VoluntaryLife
                volLife={volLife}
                childLife={childLife}
                spouseLife={spouseLife}
                reset={reset}
                attachments={attachments}
                setAttachments={setAttachments}
              />

              <div className="mt-8">
                <Header
                  title="Beneficiaries"
                  secondary={true}
                  content={
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Add16}
                      onClick={() => {
                        setBeneficiary({});
                        setShow(true);
                      }}
                    >
                      Add Beneficiary
                    </Button>
                  }
                />
                <Beneficiaries
                  show={show}
                  setShow={setShow}
                  beneficiary={beneficiary}
                  setBeneficiary={setBeneficiary}
                  type={BeneficiaryType.Primary}
                  beneficiaries={beneficiaries}
                  setBeneficiaries={setBeneficiaries}
                  setIsEmployee={setIsEmployee}
                />
                <Beneficiaries
                  show={show}
                  setShow={setShow}
                  beneficiary={beneficiary}
                  setBeneficiary={setBeneficiary}
                  type={BeneficiaryType.Contingent}
                  beneficiaries={beneficiaries}
                  setBeneficiaries={setBeneficiaries}
                  setIsEmployee={setIsEmployee}
                />
      <div className="my-4">
        This designation will apply to the following Standard Insurance Company coverage(s) if available to you through your
        Employer: Life Insurance, Life with Accidental Death &amp; Dismemberment (AD&amp;D) Insurance and, unless specified otherwise, Supplemental Life Insurance.
      </div>
      <div className="ml-8">
        <ul className="list-disc">
          <li className="py-2">
            Your designation revokes all prior designations.
          </li>
          <li className="py-2">
            If the Beneficiary is a trust or trustee, contact the Clayco benefits team.
          </li>
          <li className="py-2">
            Benefits are payable to a contingent Beneficiary only if you are not survived by one or more primary Beneficiaries.
          </li>
          <li className="py-2">
            If a minor (a person not of legal age) or your estate is the Beneficiary, it may be necessary to have a guardian or a
            legal representative appointed by the court before any death benefit can be paid. 
          </li>
          <li className="py-2">
            A power of attorney must grant specific authority, by the terms of the document or applicable law, to make or change
            a Beneficiary designation. If you have questions, consult your legal advisor.
          </li>
          <li className="py-2">
            Dependents Insurance and Supplemental Life Insurance on your Spouse, if any, is payable to you, if living, or as
            provided under your Employer's coverage under the Group Policy.          
          </li>
        </ul>
        
        </div>
          <ErrorSummary />
      </div>
            </>
          ) : (
            <StructuredListSkeleton />
          )}
        </div>
      </StepContent>

      <BeneficiaryEdit
        show={show}
        setShow={setShow}
        beneficiary={beneficiary}
        setBeneficiary={setBeneficiary}
        beneficiaries={beneficiaries}
        setBeneficiaries={setBeneficiaries}
        isEmployee={isEmployee}
        setIsEmployee={setIsEmployee}
      />
    </>
  );
};

export default LifeInsurance;
